import React, { useEffect, useMemo, useRef, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import SearchIcon from '@mui/icons-material/Search'
import { toast } from 'react-toastify'

import fetcher from '../../fetcher'
import { NewLeadModal } from './NewLeadModal'
import { PipelinePage } from './PipelinePage/PipelinePage'
import { usePipelineStore } from './stores/pipelineStore'
import { keepEscapeInOpenSelect } from './selectEscape'
import './AttachToLeadsModal.scss'

// Pick the leads a quote should be attached to, from the pipeline board itself
// rather than a second list of names — the rep already reads the board, and it
// is the only place a lead's stage, rep and quoted cars appear together.
//
// Nothing is written until Confirm. Checking a card only changes local state;
// the attach call happens once per selected lead when the footer button is
// pressed. (The person-detail AttachQuotesModal attaches in real time and
// deletes on uncheck — deliberately NOT the behaviour here.)
//
// Each attach stores a *clone* of the quote against the opportunity, so the
// original stays in the rep's Quotes list untouched; see crm/views.attach_quote.
export const AttachToLeadsModal = ({ dealId, isOpen, setIsOpen }) => {
    // id → the person's name, so the ribbon needs no second store and no second
    // fetch: the name comes off the card that was just checked.
    const [selected, setSelected] = useState(() => new Map())
    const [busy, setBusy] = useState(false)
    const [newLeadOpen, setNewLeadOpen] = useState(false)

    const search = usePipelineStore((state) => state.search)
    const setSearch = usePipelineStore((state) => state.setSearch)

    // The board's search and rep filter are module-level and deliberately
    // outlive a visit (pipelineStore). Borrowing them here would otherwise
    // follow the rep back to their real board, so the values they arrived with
    // are put back on the way out.
    const [entryFilters] = useState(() => {
        const { search: s, repFilter } = usePipelineStore.getState()
        return { search: s, repFilter }
    })
    const restoredRef = useRef(false)

    const restoreFilters = () => {
        if (restoredRef.current) return
        restoredRef.current = true
        const { search: s, repFilter } = usePipelineStore.getState()
        if (s === entryFilters.search && repFilter === entryFilters.repFilter) return
        usePipelineStore.setState({ search: entryFilters.search, repFilter: entryFilters.repFilter })
        // The board behind this modal may still be mounted (a quote opened from
        // a card), so the columns are re-read, not just the values.
        usePipelineStore.getState().applyFilters()
    }

    const close = () => {
        restoreFilters()
        setIsOpen(null)
    }

    // Safety net for a close path that bypasses `close` — the guard above makes
    // it idempotent, so this can only ever run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => () => restoreFilters(), [])

    const selection = useMemo(() => ({
        // A Map, so one piece of state answers both "is this card checked?" and
        // "whose names go in the ribbon?". The cards only ever call `.has()`.
        selectedIds: selected,
        onToggle: (opportunity) => setSelected((prev) => {
            const next = new Map(prev)
            if (next.has(opportunity.id)) next.delete(opportunity.id)
            else next.set(opportunity.id, opportunity.person_detail?.name || 'Unnamed')
            return next
        }),
    }), [selected])

    const confirm = async () => {
        if (busy || selected.size === 0) return
        setBusy(true)

        // Snapshotted: the board may repaint underneath while these are in
        // flight, and the results have to line up with what was submitted.
        const ids = [...selected.keys()]
        const n = ids.length
        const word = n === 1 ? 'lead' : 'leads'
        // No bulk endpoint exists, so this is one call per lead. allSettled
        // rather than all: one refusal must not hide which of the others landed.
        const results = await Promise.allSettled(
            ids.map((opportunityId) => fetcher.attachCrmQuote(opportunityId, dealId))
        )
        const failed = ids.filter((_, i) => results[i].status === 'rejected')
        setBusy(false)

        if (failed.length === 0) {
            toast.success(`Quote attached to ${n} ${word}.`)
            close()
            return
        }

        // What succeeded stays attached — nothing is rolled back. Only the
        // leads that failed stay checked, so the ribbon narrows to them and
        // pressing the button again retries exactly those.
        const stillFailed = new Set(failed)
        setSelected((prev) => new Map([...prev].filter(([id]) => stillFailed.has(id))))
        toast.error(`Could not attach to ${failed.length} of ${n} ${word}.`)
    }

    return (
        <Dialog
            open={Boolean(isOpen)}
            onClose={close}
            maxWidth={false}
            PaperProps={{ className: 'attach-leads-modal', onKeyDown: keepEscapeInOpenSelect }}
        >
            {/* NewLeadModal's head and foot classes are global, so this dialog
                wears the CRM's own chrome rather than a second copy of it. */}
            <div className="nl-head">
                <div className="nl-head-ic"><PersonAddAlt1Icon style={{ fontSize: 20 }} /></div>
                <div className="nl-head-text">
                    <div className="nl-title">Check cards you would like to attach this quote to</div>
                    <div className="nl-sub">
                        Each lead gets its own copy of this quote — the original stays in your Quotes list.
                    </div>
                </div>
                <button className="nl-x" onClick={close} aria-label="Close">
                    <CloseIcon style={{ fontSize: 18 }} />
                </button>
            </div>

            <div className="atl-controls">
                <div className="atl-search">
                    <SearchIcon />
                    <input
                        placeholder="Search leads…"
                        aria-label="Search leads"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <button className="crm-new-btn" onClick={() => setNewLeadOpen(true)}>
                    <AddIcon style={{ fontSize: 16 }} /> <span>New Lead</span>
                </button>
            </div>

            {/* The selection ribbon — PeoplePage's bulk bar in a different
                costume: one strip across the board naming everyone picked so
                far, so the rep can confirm without scrolling back. Reading
                only; the card's own checkbox is what removes a name. */}
            {selected.size > 0 && (
                <div className="atl-ribbon" role="status" aria-live="polite">
                    <span className="atl-ribbon-label">Selected:</span>
                    <span className="atl-ribbon-names">
                        {[...selected.values()].join(' • ')}
                    </span>
                </div>
            )}

            <div className="atl-board">
                <PipelinePage selection={selection} />
            </div>

            <div className="nl-foot">
                <button className="nl-btn-ghost" onClick={close}>Cancel</button>
                <button
                    className="nl-btn-primary"
                    onClick={confirm}
                    disabled={selected.size === 0 || busy}
                >
                    {busy ? 'Attaching…' : 'Attach Quote'}
                </button>
            </div>

            {/* Stacks above this dialog on its own. Creating a lead refreshes
                the stage it landed in (NewLeadModal does that itself), so the
                new card simply appears here — and `selected` is untouched, so
                nothing already picked is lost. The quote is NOT auto-attached:
                the rep still checks the card and confirms. */}
            <NewLeadModal open={newLeadOpen} onClose={() => setNewLeadOpen(false)} />
        </Dialog>
    )
}
